import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Card, CardContent, Typography, Alert, Chip, LinearProgress,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { TrendingUp as UpIcon, TrendingDown as DownIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { SectionCard } from './SectionCard';
import { SectionSkeleton } from './SectionSkeleton';
import { getPlatformColor, getPlatformName } from '../config/platforms';

const API_URL = process.env.REACT_APP_API_URL || '';

interface Bucket {
  bucket: string;
  value: number;
  /** Change since the oldest reading in the window; null when there is nothing to compare. */
  change: number | null;
}

interface DemoAccount {
  account_ref: string;
  account: string;
  platform: string;
  measured_at: string;
  dimensions: Record<string, Bucket[]>;
}

const DIMENSION_LABELS: Record<string, string> = {
  country: 'Country',
  city: 'City',
  age: 'Age',
  gender: 'Gender',
  // LinkedIn's breakdowns (added 2026-10-17)
  function: 'Job Function',
  seniority: 'Seniority',
  industry: 'Industry',
  region: 'Region',
  company_size: 'Company Size',
};

const GENDER_LABELS: Record<string, string> = { F: 'Women', M: 'Men', U: 'Not specified' };

const label = (dimension: string, bucket: string) =>
  dimension === 'gender' ? GENDER_LABELS[bucket] || bucket : bucket;

/**
 * Aggregate follower demographics — who follows an account, never which individuals.
 *
 * Instagram (country, city, age, gender) and, since 2026-10-17, LinkedIn (job function,
 * seniority, industry, country, region, company size). Facebook's equivalents
 * (page_fans_country, page_fans_city, page_fans_gender_age) were all removed by Meta and
 * v21.0 rejects them outright. A breakdown only one network has shows only that network.
 */
export const AudienceDemographics: React.FC<{ days?: number }> = ({ days = 90 }) => {
  const { token } = useAuth();
  const [accounts, setAccounts] = useState<DemoAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dimension, setDimension] = useState('country');

  const loadedOnce = useRef(false);

  const load = useCallback(async () => {
    // `token` is null on the first render, while AuthContext is still restoring the session
    // from storage. Fetching then sends `Bearer null`, and AuthContext's 401 interceptor
    // treats any unauthenticated API response as a dead session and signs the user out —
    // so an unguarded fetch here can log someone out just for opening the page.
    if (!token) return;
    // Spinner only on the very first load. A re-fetch — after hiding an account, say — would
    // otherwise swap a populated panel for a spinner and read as the content disappearing.
    //
    // Read through a ref rather than depending on `accounts`: making it a dependency of this
    // callback recreates it on every fetch, which re-runs the effect below and loops.
    if (!loadedOnce.current) setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/audience/demographics?days=${days}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Could not load demographics');
      const data = await res.json();
      setAccounts(data.accounts || []);
      loadedOnce.current = true;
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load demographics');
    } finally {
      setLoading(false);
    }
  }, [token, days]);

  useEffect(() => { load(); }, [load]);

  // Re-fetch when an account is hidden or unhidden anywhere on the page. Same server-side
  // list drives every audience panel, so they must not drift apart until a reload.
  useEffect(() => {
    const onHiddenChanged = () => { load(); };
    window.addEventListener('shoutvector:audience-hidden-changed', onHiddenChanged);
    return () => window.removeEventListener('shoutvector:audience-hidden-changed', onHiddenChanged);
  }, [load]);


  if (loading) return <SectionSkeleton frameless rows={4} />;
  if (error) return <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>;

  if (!accounts.length) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        <strong>No follower demographics yet.</strong> Instagram&apos;s need the{' '}
        <code>instagram_manage_insights</code> permission, which arrives the next time Facebook
        is reconnected on Platform Connections, and Instagram withholds them for accounts under
        about 100 followers. LinkedIn&apos;s are fetched once a day for the Page you publish to.
        <br /><br />
        These are aggregate counts — country, city, age, job function and so on.{' '}
        <strong>No platform discloses which individuals follow or unfollow you</strong>, so
        names are not available from any of them.
      </Alert>
    );
  }

  const available = Array.from(new Set(accounts.flatMap(a => Object.keys(a.dimensions))));
  // 'country' is shared by both networks; fall back to whatever exists if it is missing
  const shown = available.includes(dimension) ? dimension : available[0];

  return (
    <SectionCard
      frameless
      title="Who Follows You"
      actions={
        <ToggleButtonGroup
          size="small"
          exclusive
          value={shown}
          onChange={(_, v) => v && setDimension(v)}
          sx={{ flexWrap: 'wrap' }}
        >
          {available.map(d => (
            <ToggleButton key={d} value={d}>{DIMENSION_LABELS[d] || d}</ToggleButton>
          ))}
        </ToggleButtonGroup>
      }
    >

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
        {/* Only accounts that report the chosen breakdown: Job Function is LinkedIn's, Age is
            Instagram's — an empty card for the other network adds nothing */}
        {accounts.filter(acc => acc.dimensions[shown]).map(acc => {
          const buckets = (acc.dimensions[shown] || []).slice(0, 8);
          const max = Math.max(1, ...buckets.map(b => b.value));
          return (
            <Card key={`${acc.platform}-${acc.account_ref}`} variant="outlined">
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Chip size="small" label={getPlatformName(acc.platform)}
                    sx={{ bgcolor: getPlatformColor(acc.platform), color: '#fff' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{acc.account}</Typography>
                </Box>

                {buckets.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nothing reported for {DIMENSION_LABELS[shown] || shown}.
                  </Typography>
                ) : buckets.map(b => (
                  <Box key={b.bucket} sx={{ mb: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                      <Typography
                        variant="body2"
                        noWrap
                        title={label(shown, b.bucket)}
                        sx={{ flexGrow: 1, minWidth: 0 }}
                      >
                        {label(shown, b.bucket)}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {b.value.toLocaleString()}
                      </Typography>
                      {b.change !== null && b.change !== 0 && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={b.change > 0 ? 'success' : 'error'}
                          icon={b.change > 0 ? <UpIcon /> : <DownIcon />}
                          label={b.change > 0 ? `+${b.change}` : b.change}
                          sx={{ height: 20, '& .MuiChip-label': { px: 0.5, fontSize: 11 } }}
                        />
                      )}
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={(b.value / max) * 100}
                      sx={{ height: 6, borderRadius: 3, mt: 0.5 }}
                    />
                  </Box>
                ))}

                <Typography variant="caption" color="text.secondary">
                  Measured {acc.measured_at}
                </Typography>
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Alert severity="info" sx={{ mt: 2 }}>
        Aggregate counts only. <strong>No platform discloses which individuals follow or
        unfollow you</strong> — Facebook, Instagram and LinkedIn all report numbers rather than
        names, so this shows what kind of audience changed, not who.
      </Alert>
    </SectionCard>
  );
};

export default AudienceDemographics;
